import axios from 'axios';
import logService from '../services/logService';

export const handleButtonClick = async (projectDetails, event, setIsGenerating, setDownloadStatus, setErrorMessage) => {
    event.preventDefault(); // Prevent default button click refreshing

    setIsGenerating(true);
    setDownloadStatus(null);
    setErrorMessage('');

    try {
        logService.info('Sending project details to backend: ' + JSON.stringify(projectDetails, null, 2));

        // POST request
        const response = await axios.post(process.env.REACT_APP_CONNECTORS_API_URL, projectDetails, {
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 30000, // 30 second timeout
        });

        logService.info('Backend response status: ' + response.status);

        const locationUrl = response.headers.location;
        if (locationUrl) {
            // GET request
            const downloadResponse = await axios.get(locationUrl, {
                responseType: 'blob',
            });

            // Extract filename from Content-Disposition header
            const contentDisposition = downloadResponse.headers['content-disposition'];
            let filename = "downloaded.zip"; // Default filename if not found
            if (contentDisposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, ''); // Remove any surrounding quotes
                }
            }

            // Create a URL for the blob and initiate download
            const url = window.URL.createObjectURL(downloadResponse.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename; // Use the extracted filename
            document.body.appendChild(a); // Append a to body
            a.click(); // Programmatically click the a to start download
            a.remove(); // Clean up

            setDownloadStatus('success');
        } else {
            throw new Error('Location header for redirect not found');
        }
    } catch (error) {
        logService.error('Error generating project: ' + error.message, {
            source: 'HandleDownloadRequest',
            stack: error.stack,
        });

        let errorMsg = 'Failed to generate project. Please try again.';

        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            // Network timeout
            errorMsg = 'Request timed out. The server took too long to respond. Please try again.';
        } else if (error.response) {
            // Server responded with an error status
            const status = error.response.status;
            const serverMessage = error.response.data?.message || error.response.data?.error;

            switch (status) {
                case 400:
                    // Invalid form data
                    errorMsg = serverMessage
                        ? `Invalid configuration: ${serverMessage}`
                        : 'Invalid project configuration. Please check your inputs and try again.';
                    break;
                case 413:
                    // File too large
                    errorMsg = 'The generated project is too large. Please simplify your configuration and try again.';
                    break;
                case 500:
                    // Server error
                    errorMsg = 'Server error occurred while generating your project. Please try again in a few moments.';
                    break;
                case 502:
                case 503:
                case 504:
                    // Server unavailable
                    errorMsg = 'Service is temporarily unavailable. Please try again in a few minutes.';
                    break;
                default:
                    errorMsg = serverMessage
                        ? `Error: ${serverMessage}`
                        : `Server error (${status}). Please try again.`;
            }
        } else if (error.request) {
            // Request made but no response received
            errorMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
        }

        setDownloadStatus('error');
        setErrorMessage(errorMsg);
    } finally {
        setIsGenerating(false);
    }
};
